<template>
  <div id="app-header" :class="$route.name">
    <div class="header-bar">
      <div class="brand">
        <img class="brand-logo" alt="Logo de l'application" :src="logoPath" />
        <span v-if="appName" class="brand-name desktop">
          {{ appName }}
        </span>
      </div>

      <nav class="header-tabs" aria-label="Navigation principale">
        <router-link
          :to="{ name: 'index' }"
          class="header-tab"
          :class="{ 'is-active': $route.name === 'index' }"
        >
          <img class="tab-icon" :src="icons.models" alt="" />
          <span class="tab-text">Tous les projets</span>
        </router-link>

        <router-link
          v-if="showModelsTab"
          :to="{ name: 'project_type_list' }"
          class="header-tab"
          :class="{ 'is-active': $route.name === 'project_type_list' }"
        >
          <img class="tab-icon" :src="icons.projects" alt="" />
          <span class="tab-text">Modèles de projet</span>
        </router-link>

        <router-link
          v-if="moderation.visible"
          :to="{ name: 'moderation' }"
          class="header-tab"
          :class="{ 'is-active': $route.name === 'moderation' }"
        >
          <span class="tab-text">Modération</span>
          <span class="tab-badge">{{ moderation.count }}</span>
        </router-link>
      </nav>

      <div id="account-menu" class="account">
        <i
          v-if="!isOnline"
          class="wifi icon offline-icon"
          aria-label="Hors connexion"
        />

        <button
          v-if="!user && !disableLoginBtn"
          type="button"
          class="login-button"
          @click="goToLogin"
        >
          Se connecter
        </button>

        <button
          v-if="user"
          type="button"
          class="user-button"
          :aria-expanded="userMenuOpen ? 'true' : 'false'"
          aria-haspopup="menu"
          @click="userMenuOpen = !userMenuOpen"
        >
          <img class="user-icon" :src="icons.profile" alt="" />
          <span>{{ displayName }}</span>
        </button>

        <div v-if="user && userMenuOpen" class="user-menu" role="menu">
          <router-link
            class="user-menu-item"
            :to="{ name: 'my_account' }"
            role="menuitem"
            @click.native="userMenuOpen = false"
          >
            <img :src="icons.profile" alt="" />
            Mon profil
          </router-link>
          <a
            v-if="showAdministration"
            class="user-menu-item"
            :href="adminUrl"
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            @click="userMenuOpen = false"
          >
            <img :src="icons.admin" alt="" />
            Administration
          </a>
          <router-link
            class="user-menu-item"
            :to="{ name: 'aide' }"
            role="menuitem"
            @click.native="userMenuOpen = false"
          >
            <img :src="icons.help" alt="" />
            Aide Géocontrib
          </router-link>
          <div class="user-menu-separator" role="separator" />
          <button
            type="button"
            class="user-menu-item"
            role="menuitem"
            @click="logout"
          >
            <img :src="icons.logout" alt="" />
            Me déconnecter
          </button>
        </div>
      </div>
    </div>
    <MessageInfoList />
  </div>
</template>

<script>
import { mapState } from "vuex";
import axios from "@/axios-client.js";
import MessageInfoList from "@/components/MessageInfoList";
import projectsIcon from "@/assets/img/header/projects.png";
import modelsIcon from "@/assets/img/header/models.png";
import profileIcon from "@/assets/img/header/profile.png";
import adminIcon from "@/assets/img/header/admin.png";
import helpIcon from "@/assets/img/header/help.png";
import logoutIcon from "@/assets/img/header/logout.png";

export default {
  name: "AppHeader",

  components: {
    MessageInfoList,
  },

  data() {
    return {
      userMenuOpen: false,
      icons: {
        projects: projectsIcon,
        models: modelsIcon,
        profile: profileIcon,
        admin: adminIcon,
        help: helpIcon,
        logout: logoutIcon,
      },
      moderation: {
        visible: false,
        count: 0,
      },
    };
  },

  computed: {
    ...mapState(["user", "configuration", "isOnline"]),

    appName() {
      return this.configuration.VUE_APP_APPLICATION_NAME;
    },
    disableLoginBtn() {
      return this.configuration.VUE_APP_DISABLE_LOGIN_BUTTON;
    },
    ssoLogoutUrl() {
      return this.configuration.VUE_APP_LOGOUT_URL;
    },
    ssoLoginUrl() {
      if (this.configuration.VUE_APP_LOGIN_URL) {
        return `${
          this.configuration.VUE_APP_LOGIN_URL
        }?next=${encodeURIComponent(window.location.pathname)}`;
      }
      return null;
    },
    logoPath() {
      return this.configuration.VUE_APP_LOGO_PATH;
    },
    displayName() {
      if (!this.user) {
        return "";
      }
      if (this.user.first_name || this.user.last_name) {
        return `${this.user.first_name} ${this.user.last_name}`.trim();
      }
      return this.user.username;
    },
    geoStatus() {
      if (!this.user) {
        return "anonymous";
      }
      if (this.user.is_superuser) {
        return "super_admin";
      }
      if (this.user.is_administrator) {
        return "gestionnaire";
      }
      if (this.user.is_staff) {
        return "equipe";
      }
      return "actif";
    },
    showModelsTab() {
      return (
        this.geoStatus === "gestionnaire" || this.geoStatus === "super_admin"
      );
    },
    showAdministration() {
      return this.geoStatus === "equipe" || this.geoStatus === "super_admin";
    },
    adminUrl() {
      const base = process.env.BASE_URL || "/";
      return `${base}admin/`;
    },
  },

  watch: {
    user() {
      this.userMenuOpen = false;
      this.loadModeration();
    },
  },

  created() {
    window.addEventListener("mousedown", this.clickOutsideMenu);
    this.loadModeration();
  },

  beforeDestroy() {
    window.removeEventListener("mousedown", this.clickOutsideMenu);
  },

  methods: {
    goToLogin() {
      sessionStorage.setItem("geocontribLoginRedirect", this.$route.fullPath);
      if (this.ssoLoginUrl) {
        window.location.href = this.ssoLoginUrl;
        return;
      }
      this.$router.push({ name: "login" });
    },

    logout() {
      this.userMenuOpen = false;
      if (this.ssoLogoutUrl) {
        window.location.href = this.ssoLogoutUrl;
        return;
      }
      this.$store.dispatch("LOGOUT");
    },

    clickOutsideMenu(event) {
      if (event.target.closest && !event.target.closest("#account-menu")) {
        this.userMenuOpen = false;
      }
    },

    async loadModeration() {
      if (!this.user) {
        this.moderation = { visible: false, count: 0 };
        return;
      }
      const baseUrl = this.configuration.VUE_APP_DJANGO_API_BASE;
      if (!baseUrl) {
        return;
      }
      try {
        const response = await axios.get(`${baseUrl}moderation-queue/`);
        this.moderation = {
          visible: Boolean(response.data.visible),
          count: response.data.count || 0,
        };
      } catch (error) {
        this.moderation = { visible: false, count: 0 };
      }
    },
  },
};
</script>

<style lang="less" scoped>
#app-header {
  --app-header-height: 55px;
}

.header-bar {
  display: flex;
  align-items: stretch;
  gap: 1.5rem;
  min-height: var(--app-header-height);
  padding: 0 1.25rem;
  color: #fff;
}

.brand {
  display: flex;
  align-items: center;
  align-self: center;
  gap: 0.75rem;
  flex: none;
}

.brand-logo {
  height: 42px;
  width: auto;
}

.brand-name {
  font-weight: 600;
  letter-spacing: 0.01em;
}

.header-tabs {
  display: flex;
  align-items: stretch;
  gap: 0;
  min-height: var(--app-header-height);
}

.header-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0 1.15rem;
  color: #fff;
  text-decoration: none;
  border-bottom: 0;
  font-size: 0.95rem;

  &:hover,
  &.is-active,
  &.router-link-exact-active {
    background: #3a468e;
    color: #fff;
  }
}

.tab-icon {
  width: 1.6rem;
  height: 1.6rem;
  flex: none;
}

.tab-text {
  font-size: 14px;
  font-weight: 600;
  line-height: 17.3px;
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 1.45rem;
  height: 1.45rem;
  padding: 0 0.35rem;
  border: 0;
  border-radius: 999px;
  background: #c4453c;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.account {
  position: relative;
  display: flex;
  align-items: center;
  align-self: center;
  gap: 0.75rem;
  margin-left: auto;
}

.offline-icon {
  margin: 0;
  opacity: 0.85;
}

.login-button,
.user-button {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.15rem;
  border: 0;
  background: transparent;
  color: #fff;
  font: inherit;
  cursor: pointer;
}

.user-icon {
  width: 1.1rem;
  height: 1.1rem;
}

.user-menu {
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  z-index: 1001;
  width: 200px;
  padding: 0.4rem 0;
  background: #313c4e;
  border-radius: 6px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
}

.user-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.7rem 1rem;
  border: 0;
  background: transparent;
  color: #fff;
  font: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;

  img {
    width: 1.15rem;
    height: 1.15rem;
    flex: none;
    object-fit: contain;
  }

  &:hover,
  &:focus {
    background: #3a468e;
    color: #fff;
  }
}

.user-menu-separator {
  height: 1px;
  margin: 0.35rem 1rem;
  background: rgba(255, 255, 255, 0.22);
}

@media screen and (max-width: 725px) {
  #app-header {
    --app-header-height: 116px;
  }

  .header-bar {
    flex-wrap: wrap;
    align-content: center;
    padding: 0.45rem 0.9rem;
    gap: 0.35rem 1rem;
  }

  .header-tabs {
    order: 3;
    width: 100%;
    min-height: 0;
    overflow-x: auto;
  }

  .header-tab {
    padding: 0.55rem 0.7rem;
    white-space: nowrap;
  }

  .brand-name {
    display: none;
  }
}
</style>
